import {
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
	Renderer2,
} from '@angular/core';
import { CarrierTable, IS_EMPTY } from 'cargo-chief-commons';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CarrierSearchResultsComponent } from './carrier-search-results/carrier-search-results.component';
import { LaneDetailsComponent } from '../../carrier-full-profile/lane-details/lane-details.component';
import { UtilService } from '@service/util/util.service';
import { ProjectSearchResultsComponent } from './project-search-results/project-search-results.component';
import { BrokerSearchResultsComponent } from './broker-search-results/broker-search-results.component';
import { MtxPopoverTrigger } from '@ng-matero/extensions/popover';

@Component({
	selector: 'app-quick-search',
	templateUrl: './quick-search.component.html',
	styleUrls: ['./quick-search.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class QuickSearchComponent implements OnInit {
	@ViewChild(MtxPopoverTrigger) trigger: MtxPopoverTrigger;
	@ViewChild('search') search: ElementRef<HTMLInputElement>;
	@ViewChild('carrierSearchResultsComponent')
	carrierSearchResultsComponent: CarrierSearchResultsComponent;
	@ViewChild('projectSearchResultsComponent')
	projectSearchResultsComponent: ProjectSearchResultsComponent;
	@ViewChild('brokerSearchResultsComponent')
	brokerSearchResultsComponent: BrokerSearchResultsComponent;
	@Input() laneDetails: LaneDetailsComponent;
	@Output() openCarrierFullProfile: EventEmitter<any> = new EventEmitter();
	@Output() closeCarrierFullProfile: EventEmitter<any> = new EventEmitter();

	isQuickSearchClicked: boolean = false;
	quickSearchString: string = '';
	searchInputSubject: Subject<string> = new Subject();
	isFullProfileOpened: boolean = false;
	public pendingHTTPRequests: Subject<void> = new Subject<void>();
	IS_CARRIER: boolean = false;
	placeholderText: string = 'Search for carriers and projects';
	searchStringsToSkip = ['mc:', 'mc-', 'dot:', 'dot-', 'code:', 'code-'];

	constructor(public utilService: UtilService, public renderer: Renderer2) {
		this.IS_CARRIER = this.utilService.isAccountTypeCarrier();
		this.searchInputSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe((_) => {
			this.IS_CARRIER
				? this.brokerSearchResultsComponent.initiateSearch()
				: this.carrierSearchResultsComponent.initiateSearch();
			this.projectSearchResultsComponent.initiateSearch();
		});
		if (this.IS_CARRIER) {
			this.placeholderText = 'Search for brokers and projects';
		}

		this.renderer.listen('window', 'click', (e: any) => {
			const targetClassName = typeof e?.target?.className === 'string' ? e?.target?.className : '';
			const cardContainer = document.getElementById('quickSearchPopoverCard'),
				inputContainer = document.getElementById('inputContainer'),
				projectsPopover = document.getElementById('projectsPopover'),
				tasksPopover = document.getElementById('tasksPopover'),
				notesDialog = document.getElementsByClassName('mat-dialog-container'),
				overlayBackdrop = targetClassName?.indexOf('cdk-overlay-backdrop') !== -1;

			const otherPopoverOpened = projectsPopover || tasksPopover || notesDialog?.length;
			if (
				!this.isFullProfileOpened &&
				this.trigger &&
				!otherPopoverOpened &&
				!overlayBackdrop &&
				!cardContainer?.contains(e.target) &&
				!inputContainer?.contains(e.target) &&
				this.trigger['_popoverOpen']
			) {
				this.animate();
				this.closePopover();
				this.quickSearchString = '';
			}
		});
	}

	ngOnInit(): void {}

	animate() {
		this.isQuickSearchClicked = !this.isQuickSearchClicked;
		if (this.isQuickSearchClicked) {
			this.openPopover();
			this.IS_CARRIER
				? this.brokerSearchResultsComponent.fetchRecentlyViewedBrokers()
				: this.carrierSearchResultsComponent.fetchRecentlyViewedCarriers();
			this.projectSearchResultsComponent.fetchRecentlyViewedProjects();
			this.focusToSearchInput();
		} else {
			setTimeout(() => {
				if (!this.utilService.isNullOrUndefined(this.search)) {
					this.search.nativeElement.blur();
				}
			});
		}
	}

	openPopover() {
		setTimeout(() => {
			if (!this.utilService.isNullOrUndefined(this.trigger)) {
				this.trigger.openPopover();
				if (IS_EMPTY(this.quickSearchString)) {
					this.IS_CARRIER
						? this.brokerSearchResultsComponent.copyRecentlyViewedBrokers()
						: this.carrierSearchResultsComponent.copyRecentlyViewedCarrier();
				}
			}
		}, 0);
	}

	closePopover() {
		if (!this.utilService.isNullOrUndefined(this.trigger)) {
			this.trigger.closePopover();
			if (!this.isFullProfileOpened) {
				this.carrierSearchResultsComponent.carriersList = null;
			}
		}
	}

	@HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
		event.stopPropagation();
		if (!this.isFullProfileOpened && this.isQuickSearchClicked) {
			this.animate();
			this.closePopover();
			this.quickSearchString = '';
		}
	}

	openCarrierFullProfileHandler(event: CarrierTable) {
		this.openCarrierFullProfile.emit(event);
		this.isFullProfileOpened = true;
		this.closePopover();
	}

	closeCarrierFullProfileHandler() {
		this.isFullProfileOpened = false;
		if (
			!this.utilService.isNullOrUndefined(this.laneDetails) &&
			!this.utilService.isNullOrUndefined(this.laneDetails.capacitySearch) &&
			!this.utilService.isNullOrUndefined(this.laneDetails.capacitySearch.carrierId)
		) {
			if (!this.utilService.isNullOrUndefined(this.trigger)) {
				this.trigger.openPopover();
				this.focusToSearchInput();
			}
		}
		this.closeCarrierFullProfile.emit();
	}

	focusToSearchInput() {
		setTimeout(() => {
			if (!this.utilService.isNullOrUndefined(this.search)) {
				this.search.nativeElement.focus();
			}
		});
	}

	ngOnDestroy(): void {
		if (!this.utilService.isNullOrUndefined(this.pendingHTTPRequests)) {
			this.pendingHTTPRequests.next();
			this.pendingHTTPRequests.complete();
			this.pendingHTTPRequests.unsubscribe();
		}
	}

	textNotToBeSkipped(value): boolean {
		const formattedValue = value && value.trim().toLowerCase();
		return !this.searchStringsToSkip.includes(formattedValue);
	}

	onSearchChange(searchValue): void {
		this.pendingHTTPRequests.next();
		this.textNotToBeSkipped(searchValue) && this.searchInputSubject.next(searchValue);
	}
}
